import { useState } from 'react'
import { FileSpreadsheet, FileText } from 'lucide-react'
import { toast } from 'sonner'
import { ApiError, downloadFile } from '@/lib/api'
import { useI18n } from '@/i18n/I18nProvider'
import { Button } from './ui'

interface ExportButtonProps {
  path: string
  params?: Record<string, unknown>
  format: 'xlsx' | 'pdf'
  label?: string
  variant?: 'primary' | 'secondary' | 'ghost'
}

/**
 * Triggers an authenticated file download.
 *
 * The button disables itself while the export runs. Building a workbook over a
 * year of data takes real time, and without visible feedback the natural
 * response is to click again — which on a slow connection queues a second full
 * export rather than cancelling the first.
 */
export function ExportButton({
  path,
  params,
  format,
  label,
  variant = 'secondary',
}: ExportButtonProps) {
  const { t } = useI18n()
  const [busy, setBusy] = useState(false)

  const Icon = format === 'xlsx' ? FileSpreadsheet : FileText
  const text = label ?? (format === 'xlsx' ? 'Excel' : 'PDF')

  async function handleClick() {
    setBusy(true)

    try {
      await downloadFile(path, params)
      toast.success(t('export.started'))
    } catch (error) {
      // An ApiError already carries a server sentence in the reader's
      // language; only the fallback needs translating here.
      toast.error(error instanceof ApiError ? error.message : t('export.failed'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Button variant={variant} onClick={handleClick} loading={busy} disabled={busy}>
      {!busy && <Icon className="size-4" aria-hidden />}
      {text}
    </Button>
  )
}
